import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { PhysicsFriction, PhysicsMass, PhysicsRestitution } from "../physics/physics_masses.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { PhysicsCube } from "./physics_cube.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

const CONE_TEXTURES: readonly ResourceID[] = [
  ResourceID.ConeGoldenTexture,
  ResourceID.ConeRedTexture,
];

const CONE_V3_TEXTURES: readonly ResourceID[] = [
  ResourceID.ConeV3GoldenTexture,
  ResourceID.ConeV3RedTexture,
  ResourceID.ConeV3OrangeTexture,
  ResourceID.ConeV3YellowTexture,
  ResourceID.ConeV3GreenTexture,
  ResourceID.ConeV3LightBlueTexture,
  ResourceID.ConeV3BlueTexture,
  ResourceID.ConeV3PinkTexture,
];

export class PhysicsCone extends BreakablePhysicsEntity implements LevelEntity
{
  public readonly kind: EntityKind = EntityKind.Cone;

  private outside_timer = 0;

  private outside_pending = false;
  private previous_bottom_y: number | null = null;
  private previous_table_bottom_y: number | null = null;
  private drop_sound_played = false;

  private readonly far_destroy_delay = 2;

  private static readonly materials = new Map<number, THREE.MeshPhongMaterial>();

  constructor(
    position: THREE.Vector3,
    variant_index?: number,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const mesh = PhysicsCone.createMesh( variant_index );
    const size = new THREE.Box3().setFromObject( mesh ).getSize( new THREE.Vector3() );
    const collider_desc = PhysicsCone.createColliderDesc( mesh, size );

    super( mesh, position, {
      angular_damping: 0.4,
      arm_delay: 0,
      collider_desc,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
      friction: PhysicsFriction.metal_block,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      linear_damping: 0.05,
      mass: PhysicsMass.cone,
      restitution: PhysicsRestitution.metal_block,
    } );
  }

  private static createMesh( variant_index?: number ): THREE.Object3D
  {
    const is_test_cone = variant_index === 2;
    const is_v3_cone = variant_index !== undefined && variant_index >= 3;
    const texture_index = variant_index !== undefined && !is_test_cone
      ? ( ( variant_index % CONE_TEXTURES.length ) + CONE_TEXTURES.length ) % CONE_TEXTURES.length
      : Math.floor( Math.random() * CONE_TEXTURES.length );

    const visual = ResourceManager.getModel(
      is_v3_cone ? ResourceID.ConeModel : ResourceID.ConeCrownModel
    );
    const material = PhysicsCone.getMaterial( is_v3_cone ? variant_index! : is_test_cone ? 2 : texture_index );

    visual.rotation.y = Math.PI * 0.5;

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.castShadow = true;
      child.receiveShadow = false;
      child.material = material;
    } );

    visual.updateMatrixWorld( true );
    const base_box = new THREE.Box3().setFromObject( visual );
    const base_size = base_box.getSize( new THREE.Vector3() );

    const scale = PhysicsCube.getSize().y / base_size.y;
    visual.scale.setScalar( scale );

    visual.updateMatrixWorld( true );
    const visual_center = new THREE.Box3()
      .setFromObject( visual )
      .getCenter( new THREE.Vector3() );
    visual.position.sub( visual_center );

    const mesh = new THREE.Group();
    mesh.add( visual );

    return mesh;
  }

  private static getMaterial( texture_index: number ): THREE.MeshPhongMaterial
  {
    const cached = this.materials.get( texture_index );
    if ( cached ) return cached;

    const is_test_cone = texture_index === 2;
    const is_v3_cone = texture_index >= 3;
    const is_bright_v3_cone = texture_index === 5 || texture_index === 6;
    const base_map = ResourceManager.getTexture(
      is_v3_cone
        ? CONE_V3_TEXTURES[( texture_index - 3 ) % CONE_V3_TEXTURES.length]
        : CONE_TEXTURES[texture_index]
    );
    const normal_map = is_v3_cone
      ? ResourceManager.getNormalMap( ResourceID.ConeV3NormalTexture )
      : is_test_cone ? ResourceManager.getNormalMap( ResourceID.ConeTwoNormalTexture ) : null;
    base_map.anisotropy = 8;
    base_map.needsUpdate = true;
    if ( normal_map )
    {
      normal_map.anisotropy = 8;
      normal_map.needsUpdate = true;
    }

    const material = new THREE.MeshPhongMaterial( {
      map: base_map,
      normalMap: normal_map,
      normalScale: new THREE.Vector2( 0.5, 0.5 ),
      color: is_v3_cone ? 0xeeeeee : 0xffffff,
      shininess: is_v3_cone ? 45 : is_test_cone ? 30 : 80,
      specular: new THREE.Color( is_v3_cone ? 0x555555 : 0x888888 ),

      emissive: new THREE.Color( 0xffffff ),
      emissiveMap: base_map,
      emissiveIntensity: is_bright_v3_cone ? 0.06 : is_v3_cone ? 0.08 : 0.7,
    } );
    this.materials.set( texture_index, material );
    return material;
  }

  private static createColliderDesc(
    mesh: THREE.Object3D,
    size: THREE.Vector3
  ): RAPIER.ColliderDesc
  {
    const points: number[] = [];

    mesh.updateMatrixWorld( true );

    mesh.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const pos = ( child.geometry as THREE.BufferGeometry ).attributes.position;

      for ( let i = 0; i < pos.count; i++ )
      {
        const point = new THREE.Vector3(
          pos.getX( i ),
          pos.getY( i ),
          pos.getZ( i )
        ).applyMatrix4( child.matrixWorld );

        points.push(
          point.x,
          point.y,
          point.z
        );
      }
    } );

    return (
      RAPIER.ColliderDesc.convexHull( new Float32Array( points ) ) ??
      RAPIER.ColliderDesc.cylinder(
        size.y * 0.5,
        Math.min( size.x, size.z ) * 0.5
      )
    );
  }

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    if ( this.destroying )
      return;

    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();

    const table_bottom_y = table.heightAboveSurface( pos ) - this.halfHeight();

    if ( !this.drop_sound_played && this.previous_bottom_y !== null && this.previous_table_bottom_y !== null )
    {
      const landed_on_table = !is_outside
        && this.previous_table_bottom_y > 0.2
        && table_bottom_y <= 0.2;
      const landed_on_ground = is_outside
        && this.previous_bottom_y > 0.2
        && bottom_y <= 0.2;

      if ( landed_on_table || landed_on_ground )
      {
        this.playDropSound( landed_on_ground ? 0.5 : 1 );
        this.drop_sound_played = true;
      }
    }
    this.previous_bottom_y = bottom_y;
    this.previous_table_bottom_y = table_bottom_y;

    if ( is_outside && bottom_y < 0.2 )
    {
      this.destroy();
      return;
    }

    if ( !is_outside )
    {
      this.outside_pending = false;
    }
    else if ( !this.outside_pending && table.isBelowSurface( pos ) )
    {
      this.outside_pending = true;
      this.outside_timer = this.far_destroy_delay;
    }

    if ( this.outside_pending )
    {
      this.outside_timer -= dt;

      if ( this.outside_timer <= 0 )
      {
        this.destroy();
      }
    }
  }

  public playHitSound(): void
  {
    void audioController.playSound( AudioClips.WOOD_CUBE_HIT );
  }

  public playDropSound( volume = 1 ): void
  {
    const use_first_variant = Math.random() < 0.5;
    const clip = use_first_variant
      ? AudioClips.WOOD_CUBE_DROP_1
      : AudioClips.WOOD_CUBE_DROP_2;

    void audioController.playSound( clip, false, volume );
  }

  public canBeHit(): boolean
  {
    return this.isAlive();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    this.playHitSound();
    Vfx3DManager.addCannonBallBlockImpact( point, BlockImpactMaterial.METAL );
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    this.destroy();
  }

  public checkSelfDestruct( dt: number, table: PhysicsTable ): void
  {
    this.checkDestroy( dt, table );
  }

  public isAtRest(): boolean
  {
    return !this.outside_pending && super.isAtRest();
  }

  private fade_timer = 0;
  private static readonly FADE_DURATION = 0.35;

  private setOpacity( opacity: number ): void
  {
    this.mesh.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        if ( !child.userData.materialCloned )
        {
          if ( Array.isArray( child.material ) )
          {
            child.material = child.material.map( ( m: THREE.Material ) => m.clone() );
          }
          else
          {
            child.material = child.material.clone();
          }
          child.userData.materialCloned = true;
        }

        const mats = Array.isArray( child.material ) ? child.material : [ child.material ];
        for ( const mat of mats )
        {
          mat.transparent = true;
          mat.opacity = opacity;
          mat.depthWrite = false;
        }
        child.castShadow = false;
      }
    } );
  }

  protected updateDestroying( dt: number ): void
  {
    this.fade_timer -= dt;
    this.setOpacity( Math.max( 0, this.fade_timer / PhysicsCone.FADE_DURATION ) );
    if ( this.fade_timer <= 0 ) this.markDead();
  }

  public destroy(): void
  {
    if ( !this.beginDestroying() ) return;
    this.fade_timer = PhysicsCone.FADE_DURATION;
  }
}
